import { Button, CampoDeTexto, cn } from "@base/ui";
import { BriefcaseBusiness, LoaderCircle, Phone, User } from "lucide-react";
import { useState, type FormEvent } from "react";
import { ErroDaApi } from "../../../shared/http/erro-da-api";
import { mensagemDoErro } from "../../../shared/http/mensagem-de-erro";
import { useSalvarPerfil } from "../application/use-usuarios";

/** Cargos mais comuns numa clinica, como sugestao; o campo aceita qualquer texto. */
const CARGOS_SUGERIDOS = [
  "Proprietário(a)",
  "Sócio(a)",
  "Gerente",
  "Coordenador(a)",
  "Comercial",
  "Recepção",
  "Marketing",
  "Médico(a)",
  "Dentista",
  "Equipe Monte",
];

/** "11987654321" -> "(11) 98765-4321", enquanto a pessoa digita. */
export function formatarTelefone(valor: string): string {
  const digitos = valor.replace(/\D/g, "").replace(/^55(?=\d{10,11}$)/, "").slice(0, 11);
  if (digitos.length <= 2) return digitos.length ? `(${digitos}` : "";
  if (digitos.length <= 6) return `(${digitos.slice(0, 2)}) ${digitos.slice(2)}`;
  if (digitos.length <= 10) return `(${digitos.slice(0, 2)}) ${digitos.slice(2, 6)}-${digitos.slice(6)}`;
  return `(${digitos.slice(0, 2)}) ${digitos.slice(2, 7)}-${digitos.slice(7)}`;
}

/**
 * Nome, telefone/WhatsApp e cargo da propria pessoa. Usado no primeiro acesso
 * e em Meu perfil. O e-mail aparece, mas nao se edita (e o login).
 */
export function FormularioDePerfil({
  inicial,
  textoDoBotao,
  aoSalvar,
  campoGrande = false,
}: {
  readonly inicial: { nome: string; email: string; telefone: string | null; cargo: string | null };
  readonly textoDoBotao: string;
  readonly aoSalvar: () => void;
  /** Campos em pilula, no estilo da tela de acesso. */
  readonly campoGrande?: boolean;
}) {
  const [nome, setNome] = useState(inicial.nome);
  const [telefone, setTelefone] = useState(formatarTelefone(inicial.telefone ?? ""));
  const [cargo, setCargo] = useState(inicial.cargo ?? "");
  const salvar = useSalvarPerfil();
  const campos = salvar.error instanceof ErroDaApi ? salvar.error.campos : {};

  function enviar(evento: FormEvent) {
    evento.preventDefault();
    salvar.mutate({ nome, telefone, cargo }, { onSuccess: aoSalvar });
  }

  return (
    <form onSubmit={enviar} noValidate className="flex flex-col gap-5">
      <CampoDeTexto
        rotulo="Nome completo"
        value={nome}
        onChange={(evento) => setNome(evento.target.value)}
        autoComplete="name"
        icone={<User aria-hidden="true" />}
        erros={campos.nome}
        required
      />
      <CampoDeTexto
        rotulo="E-mail"
        value={inicial.email}
        readOnly
        dica="É o seu login. Para trocar, fale com a equipe Monte."
      />
      <CampoDeTexto
        rotulo="Telefone / WhatsApp"
        value={telefone}
        onChange={(evento) => setTelefone(formatarTelefone(evento.target.value))}
        inputMode="tel"
        autoComplete="tel"
        placeholder="(11) 98765-4321"
        icone={<Phone aria-hidden="true" />}
        erros={campos.telefone}
        required
      />
      <CampoDeTexto
        rotulo="Cargo na clínica"
        value={cargo}
        onChange={(evento) => setCargo(evento.target.value)}
        list="cargos-sugeridos"
        placeholder="Ex.: Gerente, Recepção, Comercial"
        icone={<BriefcaseBusiness aria-hidden="true" />}
        erros={campos.cargo}
        required
      />
      <datalist id="cargos-sugeridos">
        {CARGOS_SUGERIDOS.map((sugestao) => (
          <option key={sugestao} value={sugestao} />
        ))}
      </datalist>

      {salvar.isError && Object.keys(campos).length === 0 ? (
        <p role="alert" className="text-sm text-destructive">
          {mensagemDoErro(salvar.error)}
        </p>
      ) : null}

      <Button
        type="submit"
        disabled={salvar.isPending || nome.trim().length < 3 || telefone.replace(/\D/g, "").length < 10 || cargo.trim().length < 2}
        className={cn(campoGrande && "h-13 w-full rounded-full text-sm font-bold")}
      >
        {salvar.isPending ? <LoaderCircle className="animate-spin" aria-hidden="true" /> : null}
        {textoDoBotao}
      </Button>
    </form>
  );
}
